import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  searchFreelancerProfiles,
  createProjectFreelancerAssociation,
  bulkInviteFreelancers,
  getFreelancersByProject,
  deleteAssociation,
} from '../api/project-freelancer-profiles-api';
import { useSnackbarContext } from '../contexts/useSnackBarContext';
import { FreelancerProfileSearchRequest } from '../models/requests/FreelancerProfileSearchRequest';
import { ProjectFreelancerProfileStatus } from '../models/enums/ProjectFreelancerProfileStatus';
import { getProjectProposals, bulkTransmitToClient } from '../api/proposal-workflow-api';
import i18n from '../i18n/i18n';

// Search freelancers hook
export const useSearchFreelancers = (searchRequest: FreelancerProfileSearchRequest) => {
  return useQuery({
    queryKey: ['freelancerProfiles', 'search', searchRequest],
    queryFn: () => searchFreelancerProfiles(searchRequest),
    staleTime: 5 * 60 * 1000, // 5 minutes
    enabled: true,
  });
};

// Get freelancers by project
export const useFreelancersByProject = (projectId: number) => {
  return useQuery({
    queryKey: ['projectFreelancerProfiles', 'project', projectId],
    queryFn: () => getFreelancersByProject(projectId),
    enabled: !!projectId,
  });
};

// Create association mutation
export const useCreateProjectFreelancerAssociation = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbarContext();

  return useMutation({
    mutationFn: createProjectFreelancerAssociation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectFreelancerProfiles'] });
      showSnackbar({ message: i18n.t('project.freelancer.associateSuccess'), severity: 'success' });
    },
    onError: (error: any) => {
      showSnackbar({ message: error.message || i18n.t('project.freelancer.associateError'), severity: 'error' });
    },
  });
};

// Bulk invite mutation
export const useBulkInviteFreelancers = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbarContext();

  return useMutation({
    mutationFn: bulkInviteFreelancers,
    onSuccess: (_, variables) => {
      // Invalidate all project freelancer queries for this project
      queryClient.invalidateQueries({
        queryKey: ['projectFreelancerProfiles', 'project', variables.projectId]
      });
      queryClient.invalidateQueries({
        queryKey: ['projectFreelancerProfiles', 'clientView', variables.projectId]
      });
      showSnackbar({ message: i18n.t('project.freelancer.inviteSuccess'), severity: 'success' });
    },
    onError: (error: any) => {
      showSnackbar({ message: error.message || i18n.t('project.freelancer.inviteError'), severity: 'error' });
    },
  });
};

// Delete association mutation
export const useDeleteAssociation = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbarContext();

  return useMutation({
    mutationFn: deleteAssociation,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projectFreelancerProfiles'] });
      showSnackbar({ message: i18n.t('project.freelancer.removeSuccess'), severity: 'success' });
    },
    onError: (error: any) => {
      showSnackbar({ message: error.message || i18n.t('project.freelancer.removeError'), severity: 'error' });
    },
  });
};

// Bulk transmit proposals to client mutation
export const useBulkTransmitToClient = () => {
  const queryClient = useQueryClient();
  const { showSnackbar } = useSnackbarContext();

  return useMutation({
    mutationFn: bulkTransmitToClient,
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['projectFreelancerProfiles'] });
      const skippedCount = data.totalCount - data.successCount;
      if (skippedCount > 0 && data.successCount > 0) {
        showSnackbar({
          message: i18n.t('project.bulkTransmitPartialSuccess', {
            successCount: data.successCount,
            skippedCount
          }),
          severity: 'warning'
        });
      } else if (data.successCount > 0) {
        showSnackbar({
          message: i18n.t('project.bulkTransmitSuccess', { count: data.successCount }),
          severity: 'success'
        });
      } else {
        showSnackbar({
          message: i18n.t('project.bulkTransmitNoneEligible'),
          severity: 'warning'
        });
      }
    },
    onError: (error: any) => {
      showSnackbar({
        message: error.message || i18n.t('project.bulkTransmitError'),
        severity: 'error'
      });
    },
  });
};

// Get freelancers with submitted proposals for client view
export const useClientViewFreelancers = (projectId: number, isClientView: boolean = false, shouldFetch: boolean = true) => {
  return useQuery({
    queryKey: ['projectFreelancerProfiles', 'clientView', projectId, isClientView],
    queryFn: async () => {
      if (!isClientView) {
        // For CSM/Admin, get all associated freelancers (has correct freelancerName)
        const associations = await getFreelancersByProject(projectId);

        // Get proposals to have the correct status
        const proposals = await getProjectProposals(projectId);

        // Create a map of proposals by freelancerProfileId
        const proposalsMap = new Map(
          (proposals || []).map((p: any) => [p.freelancerProfileId, p])
        );

        // Enrich associations with proposal status
        return (associations || []).map((assoc: any) => {
          const proposal = proposalsMap.get(assoc.freelancerProfileId);
          return {
            ...assoc,
            status: proposal?.status || assoc.status,
            proposal: proposal
          };
        });
      }

      // For clients, get only freelancers with proposals submitted to client
      // The backend filters proposals based on ViewPermissions (RefusedTalent not visible to clients)
      const proposals = await getProjectProposals(projectId);

      // Convert proposals to ProjectFreelancerProfile format
      return (proposals || []).map((proposal: any) => ({
        id: proposal.freelancerProfileId,
        projectId: projectId,
        projectName: '',
        projectNumber: '',
        freelancerProfileId: proposal.freelancerProfileId,
        freelancerName: proposal.freelancerName || '',
        profileTitle: proposal.freelancerProfile?.title || '',
        status: proposal.status || ProjectFreelancerProfileStatus.SubmittedClient,
        createdAt: proposal.createdAt,
        updatedAt: proposal.updatedAt,
        createdByName: '',
        freelancerProfileKey: proposal.freelancerProfile?.key || proposal.freelancerProfileKey || '',
        freelancerProfile: {
          id: proposal.freelancerProfileId,
          freelancer: {
            user: {
              firstName: proposal.freelancerName?.split(' ')[0] || '',
              lastName: proposal.freelancerName?.split(' ').slice(1).join(' ') || '',
            }
          }
        },
        proposal: proposal
      }));
    },
    enabled: !!projectId && shouldFetch, // Only fetch if projectId exists and user is authorized
  });
};